import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/base/MovieCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { useFavorites } from "@/hooks/useFavorites";

export default function Detail() {
  const { id } = useParams();
  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title}（${movie.year}）详情 - 泡泡影视`;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [movie]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 pt-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类", to: "/category" }, { label: "内容不存在" }]} />
        <div className="mt-8">
          <EmptyState
            title="没有找到这部影视"
            description="它可能已被移除，或者链接有误。回到影视分类页看看其他精彩内容吧。"
            actionLabel="返回影视分类"
            onAction={() => window.REACT_APP_NAVIGATE("/category")}
          />
        </div>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);

  const related = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => ({
      movie: m,
      score:
        (m.category === movie.category ? 2 : 0) +
        m.genres.filter((g) => movie.genres.includes(g)).length +
        m.tags.filter((t) => movie.tags.includes(t)).length,
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map((item) => item.movie);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("已复制分享链接，快去分享给朋友吧");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="w-full">
      <div className="relative w-full h-[280px] md:h-[420px] bg-foreground-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} 影视详情`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-primary-950/70 to-primary-950/40" />
      </div>

      <div className="w-full px-4 md:px-8 -mt-28 md:-mt-40 relative z-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视分类", to: "/category" },
            { label: movie.title },
          ]}
        />

        <div className="mt-4 flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-200 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 海报`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-500 px-3 py-0.5 text-xs font-medium text-primary-950">
                {movie.category}
              </span>
              {movie.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-0.5 text-xs text-secondary-900">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
              {movie.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-700">
              <span className="flex items-center gap-1 font-semibold text-accent-600">
                <i className="ri-star-fill" />
                {movie.rating.toFixed(1)} 分
              </span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.duration}</span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?cat=${encodeURIComponent(movie.category)}`}
                  className="rounded-md border border-background-300 px-3 py-1 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  toggleFavorite(movie.id);
                  setToast(favorited ? "已取消收藏" : "收藏成功，可在本机保存");
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                    : "bg-primary-600 text-background-50 hover:bg-primary-700"
                }`}
              >
                <i className={favorited ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-line text-lg" />
                分享
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-7">
                {movie.synopsis}
              </p>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">主创团队</h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                  <dd className="text-foreground-900 font-medium">{movie.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-900 font-medium">{movie.cast.join(" / ")}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">时长</dt>
                  <dd className="text-foreground-900 font-medium">{movie.duration}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">地区</dt>
                  <dd className="text-foreground-900 font-medium">{movie.region}</dd>
                </div>
              </dl>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">所属分类</h2>
              <p className="mt-2 text-sm text-foreground-600">
                该作品归属于「{movie.category}」分类，点击查看同类更多资源。
              </p>
              <Link
                to={`/category?cat=${encodeURIComponent(movie.category)}`}
                className="mt-4 flex items-center justify-between gap-2 px-4 py-3 rounded-md bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                  <i className="ri-folder-3-line text-lg text-primary-700" />
                  {movie.category}
                </span>
                <i className="ri-arrow-right-s-line text-xl text-foreground-400" />
              </Link>
              <div className="mt-4 pt-4 border-t border-background-200 text-xs text-foreground-500 leading-relaxed">
                本页为影视内容演示，相关数据仅供浏览参考，不提供在线播放与下载。
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] animate-fade-up">
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm shadow-none">
            <i className="ri-checkbox-circle-fill text-accent-400" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}